import React, { FunctionComponent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import {
  AdminApi,
  AdminGroup,
  AdminGroupPermission,
  AdminGroupPermissionsListOrderingEnum,
  AdminGroupPermissionsValuesRetrieveFieldEnum,
  AdminIssuerDetailPermissionsEnum,
  AdminOrganizationDetails,
  DistributionPlatformPermissionEnum,
  EnvironmentPermissionsEnum,
  OrganizationPermissionsEnum,
  PermissionTypeEnum,
  ProductPermissionEnum,
} from 'src/api';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { I18NContext } from 'src/ui/i18n/provider';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { currentUserHasManageEnvironmentPermission } from 'src/apps/issuer/helpers/userPermissions';
import DeletePermissionModal from '../delete-permission-modal';
import useApiCall from 'src/hooks/use-api-call';
import { currentOrganizationHasManageOrganizationPermission } from 'src/apps/issuer/helpers/organizationPermissions';

export interface PermissionsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  permissions?: AdminGroupPermission[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  setPermissionModalOpen: (val: AdminGroupPermission) => void;
  hasPermission: boolean;
  groupOrganization: AdminOrganizationDetails | null;
  setFilterPopupHeight?: (height: number) => void;
  showDeleteButton?: boolean;
  groupId?: string;
  userId?: string;
}

const PermissionsTable: FunctionComponent<PermissionsTableProps> = (props) => {
  const {
    className,
    loading,
    ordering,
    onOrderBy,
    permissions,
    hasPermission,
    paginationProps,
    setPermissionModalOpen,
    groupOrganization,
    setFilterPopupHeight,
    showDeleteButton,
    groupId,
    userId,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const { activeLocale } = useContext(I18NContext);
  const { currentUser } = useCurrentUserSelector();

  const getPermissionTypeOptions = useMemo(() => {
    const options = [];

    if (currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) {
      options.push({
        name: translate('permissionTypes.environment'),
        id: 'ENVIRONMENT',
      });
    }

    if (
      currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) ||
      (groupOrganization && currentOrganizationHasManageOrganizationPermission(groupOrganization))
    ) {
      options.push(
        {
          name: translate('permissionTypes.organization'),
          id: 'ORGANIZATION',
        },
        {
          name: translate('permissionTypes.product'),
          id: 'PRODUCT',
        },
        {
          name: translate('permissionTypes.issuer'),
          id: 'ISSUER',
        },
        {
          name: translate('permissionTypes.distributionPlatform'),
          id: 'DISTRIBUTION_PLATFORM',
        },
      );
    }

    return options;
  }, [currentUser?.permissions, groupOrganization, translate]);

  const getPermissionCodeFilterOptions = useMemo(() => {
    const permissionsCodes = [];

    if (currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) {
      permissionsCodes.push({
        values: Object.values(EnvironmentPermissionsEnum),
        prefixKey: 'permissionTypes.environment',
      });
    }
    if (
      currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) ||
      (groupOrganization && currentOrganizationHasManageOrganizationPermission(groupOrganization))
    ) {
      permissionsCodes.push(
        {
          values: Object.values(OrganizationPermissionsEnum),
          prefixKey: 'permissionTypes.organization',
        },
        {
          values: Object.values(ProductPermissionEnum),
          prefixKey: 'permissionTypes.product',
        },
        {
          values: Object.values(AdminIssuerDetailPermissionsEnum),
          prefixKey: 'permissionTypes.issuer',
        },
        {
          values: Object.values(DistributionPlatformPermissionEnum),
          prefixKey: 'permissionTypes.distributionPlatform',
        },
      );
    }

    const options = permissionsCodes.flatMap(({ values, prefixKey }) => {
      const prefixLabel = translate(prefixKey);

      return values.map((permissionCode) => ({
        id: permissionCode,
        name: `${prefixLabel} - ${translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`)}`,
      }));
    });

    // sort alphabetically
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [currentUser?.permissions, groupOrganization, translate]);

  const getValidForOptions = useCallback(
    (permissionsObjectsOptions: string[]) => {
      const regex = /\{\{(.*?)\}\}/g;
      const a = permissionsObjectsOptions.map((str) => {
        const replacedString = str.replace(regex, (match, capturedWord) =>
          translate(`permissionTypes.${snakeToCamelCaseWithNumbers(capturedWord.toLowerCase())}`),
        );
        return {
          value: str,
          label: replacedString,
          name: replacedString,
        };
      });

      return a;
    },
    [translate],
  );

  return (
    <div className={cx('group-permissions-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={permissions}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        setFilterPopupHeight={setFilterPopupHeight}
        cells={[
          {
            title: <Translate name="issuerPermissionsTable.type" />,
            render: ({ permissionType }) => translate(`permissionTypes.${snakeToCamelCaseWithNumbers(permissionType)}`),
            orderingFieldName: AdminGroupPermissionsListOrderingEnum.PERMISSION_TYPE_ASC,
            type: CellTypeEnum.text,
            filterName: 'permissionType',
            getFilterData: () => {
              return getPermissionTypeOptions;
            },
          },
          {
            title: <Translate name="issuerPermissionsTable.permission" />,
            render: ({ permissionCode }) => translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
            orderingFieldName: AdminGroupPermissionsListOrderingEnum.PERMISSION_CODE_ASC,
            type: CellTypeEnum.text,
            filterName: 'permissionCode',
            getFilterData: () => {
              return getPermissionCodeFilterOptions;
            },
          },
          {
            title: <Translate name="issuerPermissionsTable.validFor" />,
            render: ({ permissionType, validForName }) => {
              return permissionType === PermissionTypeEnum.ENVIRONMENT ? (
                <Translate name="permissionTypes.environment" />
              ) : (
                validForName
              );
            },
            orderingFieldName: AdminGroupPermissionsListOrderingEnum.VALID_FOR_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'validForName',
            searchFieldName: AdminGroupPermissionsValuesRetrieveFieldEnum.VALID_FOR_NAME,
            getFilterData: (params: any) =>
              adminApi.adminGroupPermissionsValuesRetrieve({
                ...params,
                ...(props.groupId && { groupId: groupId }),
                ...(props.userId && { userId: userId }),
              }),
            processFilterData: (data: string[]) => getValidForOptions(data),
          },
          ...(showDeleteButton
            ? [
                {
                  title: <Translate name="issuerPermissionsTable.actions" />,
                  render: (permission: AdminGroupPermission) => (
                    <ActionButtons align="center" spacingTop="none">
                      <Button
                        variant="secondary"
                        size="xsmall"
                        onClick={() => setPermissionModalOpen(permission)}
                        disabled={!permission.canRemove}
                      >
                        <Translate name="common.remove" />
                      </Button>
                    </ActionButtons>
                  ),
                  width: activeLocale === 'de' ? 122 : 110,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

interface GroupPermissionsTableProps {
  title?: ReactNode;
  filters?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  group?: AdminGroup;
  groupId?: string;
  userId?: string;
  skipFilterUrlSync?: boolean;
  setDeletePermissionSuccessModalOpen?: (value: boolean) => void;
  hasPermission: boolean;
  groupOrganization: AdminOrganizationDetails | null;
  setFilterPopupHeight?: (height: number) => void;
  showDeleteButton?: boolean;
}

const GroupPermissionsTable: FunctionComponent<GroupPermissionsTableProps> = WithDataRecord<
  AdminApi,
  AdminGroupPermission
>(AdminApi)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    paginationProps,
    setForceDataLoad,
    setDeletePermissionSuccessModalOpen,
    group,
    hasPermission,
    groupOrganization,
    setFilterPopupHeight,
    showDeleteButton,
    groupId,
    userId,
  }) => {
    const [permissionModalOpen, setPermissionModalOpen] = useState<AdminGroupPermission | undefined>();

    return (
      <>
        <PermissionsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          permissions={data}
          loading={loading}
          paginationProps={paginationProps}
          setPermissionModalOpen={setPermissionModalOpen}
          hasPermission={hasPermission}
          groupOrganization={groupOrganization}
          setFilterPopupHeight={setFilterPopupHeight}
          showDeleteButton={showDeleteButton}
          groupId={groupId}
          userId={userId}
        />
        {permissionModalOpen && setForceDataLoad && setDeletePermissionSuccessModalOpen && group && (
          <DeletePermissionModal
            onDelete={() => {
              setForceDataLoad(true);
              setPermissionModalOpen(undefined);
              setDeletePermissionSuccessModalOpen(true);
            }}
            onClose={() => setPermissionModalOpen(undefined)}
            permission={permissionModalOpen}
            groupName={group.name}
          />
        )}
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    const { groupId, userId, ...remainingProps } = props;

    return api.adminGroupPermissionsList({
      offset,
      limit,
      ordering: ordering as AdminGroupPermissionsListOrderingEnum,
      ...remainingProps,
      ...(props.groupId && { groupId: [props.groupId] }),
      ...(props.userId && { userId: [props.userId] }),
    });
  },
);

export default GroupPermissionsTable;
